import { ActionContextType, ActionsContext, MxAction } from '../../Shell/Pandora/model/action';

import type { IComponentController, IScope } from 'angular';
import template from './dashboard-control-panel.component.html';
import type { I18n } from '../../i18n/mx-internationalization';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { AutoRefreshInterval, AutoSwitchToNextDashboardInterval } from '../dashboard-personalization.service';
import type { Dashboard, DashboardService } from '../dashboard.service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { DataModelCacheService } from '../../Shell/PandoraSolutionBuilderAgent/services/data-model-cache.service';
import type { ConfigService } from '../../App/services/mx-config-service';
import type { WizardService } from '../../Shell/PandoraSolutionBuilderAgent/services/wizard-service';
import { FeatureTrackingType, FeatureTrackingUseCases, WizardMode } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import type { SidePanelService } from '../../Shell/Pandora/services/mx-side-panel-service';
import type { EntityService } from '../../Shell/PandoraSolutionBuilderAgent/services/entity-service';
import { DashboardExportWizardController } from '../dashboard-export-wizard/dashboard-export-wizard.component';
import dashboardExportWizardTemplate from '../dashboard-export-wizard/dashboard-export-wizard.component.html';
import { DashboardPrintWizardController } from '../dashboard-print-wizard/dashboard-print-wizard.component';
import dashboardPrintWizardTemplate from '../dashboard-print-wizard/dashboard-print-wizard.component.html';
import type { AnyObject } from '../../Types';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { MxNavigationService } from '../../Navigation/mx-navigation-service';
import { DisplayMode } from '../../Shell/Pandora/model/display-modes';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import { DialogOpenerService } from '../../Shell/PandoraSolutionBuilderAgent/services/dialog-opener-service';
import type { MxFeatureTrackingService } from '../../App/services/mx-feature-tracking-service';
import type { RoutingService } from '../../App/services/mx-routing-service';

interface AutoRefreshOption {
	Title: string;
	Value: AutoRefreshInterval;
}

interface AutoSwitchToNextDashboardOption {
	Title: string;
	Value: AutoSwitchToNextDashboardInterval;
}

export class DashboardControlPanelController implements IComponentController {
	page!: DashboardPageController;
	actions: MxAction[] = [];
	autoRefreshOptions: AutoRefreshOption[] = [];
	autoSwitchToNextDashboardOptions: AutoSwitchToNextDashboardOption[] = [];
	isSwitchToNextDashboardAvailable: boolean = false;
	autoSwitchingToNextDashboardTimeoutId: ReturnType<typeof setTimeout> | undefined;
	isDashboardLicensed: boolean = false;

	static readonly $inject = [
		'$scope',
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.sb.Dashboard.Service',
		'mx.shell.NotificationService',
		'mx.SolutionBuilderAgent.DataModelCacheService',
		'mx.shell.Config',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.shell.UserProfileService',
		'mx.shell.NavigationService',
		'mx.shell.EventService',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.shell.FeatureTrackingService',
		'mx.shell.RoutingService',
	];

	constructor(
		private readonly $scope: IScope,
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService,
		private readonly notificationService: NotificationService,
		private readonly dataModelCacheService: DataModelCacheService,
		private readonly configService: ConfigService,
		private readonly wizardService: ReturnType<typeof WizardService>,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly entityService: EntityService,
		private readonly userProfileService: UserProfileService,
		private readonly navigationService: MxNavigationService,
		private readonly eventService: EventService,
		private readonly dialogOpenerService: ReturnType<typeof DialogOpenerService>,
		private readonly featureTrackingService: MxFeatureTrackingService,
		private readonly routingService: RoutingService
	) {
		this.autoRefreshOptions = [
			{ Value: 0, Title: this.i18n.get('shell.common.off') },
			{ Value: 30, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 30 }) },
			{ Value: 60, Title: this.i18n.get('shell.dashboards.autoRefresh.everyHour') },
			{ Value: 120, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', { hours: 2 }) },
			{ Value: 240, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', { hours: 4 }) },
			{ Value: 1440, Title: this.i18n.get('shell.dashboards.autoRefresh.everyDay') },
		];

		this.autoSwitchToNextDashboardOptions = [
			{ Value: 0, Title: this.i18n.get('shell.common.off') },
			{ Value: 30, Title: this.i18n.getFormatted('shell.common.interval.everySeconds', { seconds: 30 }) || 'Every 30 seconds' },
			{ Value: 60, Title: this.i18n.getFormatted('shell.common.interval.everyMinute') || 'Every minute' },
			{ Value: 120, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 2 }) },
			{ Value: 300, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 5 }) },
			{ Value: 600, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 10 }) },
			{ Value: 1200, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 20 }) },
			{ Value: 1800, Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', { minutes: 30 }) },
			{ Value: 3600, Title: this.i18n.get('shell.dashboards.autoRefresh.everyHour') },
		];

		this.initSwitchingToNeighborDashboardsHandlers();
	}

	async $onInit() {
		this.isDashboardLicensed = await this.dashboardService.isLicensed();
		this.actions = await this.getActions();

		this.eventService.dispatchEvent(EventCodes.ActionsReady, { actions: this.actions });
	}

	$onDestroy() {
		this.dataModelCacheService.enable();
		this.destroySwitchingToNeighborDashboardsHandlers();
	}

	private async initActions() {
		this.actions = await this.getActions();
		this.eventService.dispatchEvent(EventCodes.ActionsReady, { actions: this.actions });
	}

	async onSaveClick() {
		await this.page.disableConfigurationMode();
		await this.initActions();
		this.dataModelCacheService.enable();

		// If the dashboard we were editing is now hidden, navigate to the first visible sibling.
		const navItem = await this.navigationService.getNavigationItem(this.page.dashboard.navigationId);
		if (navItem?.isHidden && navItem.parent) {
			const firstVisible = navItem.parent.children?.find((c) => !c.isHidden);
			if (firstVisible) {
				this.routingService.go('dashboard', { navigationId: firstVisible.parameters?.navigationId ?? firstVisible.id });
			}
		}
	}

	private async showConfirmation(confirmationTitle: string, confirmationMessage: string): Promise<boolean> {
		try {
			await this.notificationService.confirm(confirmationTitle, confirmationMessage);
			return true;
		} catch {
			return false;
		}
	}

	private getAction(action: MxAction | undefined, exec: () => void, popover?: string, onPopoverClose?: () => void) {
		if (!action) {
			return null;
		}

		const execute = async () => {
			const isConfirmed =
				!action.serviceOperation?.ConfirmationMessage ||
				(await this.showConfirmation(action.customConfirmationTitle ?? action.title, (action.serviceOperation?.ConfirmationMessage || '') as string));

			if (isConfirmed) {
				exec();
			}
		};

		return this.createAction({
			name: action.name,
			title: action.title,
			icon: action.icon,
			execute,
			popover: popover,
			onPopoverClose: onPopoverClose,
		});
	}

	private getActionByPrefix(actions: MxAction[], prefix: string, exec: () => void, popover?: string, onPopoverClose?: () => void) {
		const action = this.getOriginalAction(actions, prefix);
		return action ? this.getAction(action, exec, popover, onPopoverClose) : null;
	}

	private getOriginalAction(actions: MxAction[], prefix: string) {
		return actions.find((action) => action.name.startsWith(prefix));
	}

	private getOpenWizardAction(actions: MxAction[], prefix: string, dashboard: Dashboard) {
		const action = this.getOriginalAction(actions, prefix);
		const wizardId = action?.Widget?.Id;
		if (!wizardId) {
			console.error('No wizard found for action:', action);
			return null;
		}

		return this.getAction(action, async () => {
			await this.showWizard({
				navigationId: dashboard.navigationId,
				widgetId: wizardId,
				widgetName: action.title,
				actionName: action.name,
				presetFields: {
					Context: {
						DashboardData: {
							filters: dashboard.filters.values,
							title: dashboard.title ?? '',
							navigationId: dashboard.navigationId,
						},
					},
				},
			});
		});
	}

	private getSubscribeAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.Subscribe', dashboard);
	}

	private getPrintAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Print', async () => {
			await this.notificationService.customDialog({
				controllerAs: 'vm',
				controller: DashboardPrintWizardController,
				template: dashboardPrintWizardTemplate,
				locals: {
					dashboard,
				},
				bindToController: true,
			});
		});
	}

	private getExportAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Export', async () => {
			await this.notificationService.customDialog({
				controllerAs: 'vm',
				controller: DashboardExportWizardController,
				template: dashboardExportWizardTemplate,
				locals: {
					dashboard,
				},
				bindToController: true,
			});
		});
	}

	private getSendAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.SendReport', dashboard);
	}

	private getShareAction(actions: MxAction[], dashboard: Dashboard) {
		return !dashboard.owner || dashboard.isOwner || dashboard.isEditable ? this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.Share', dashboard) : null;
	}

	private getForceReloadAction(actions: MxAction[]) {
		const isRefreshPopoverHidden = this.dashboardService.isRefreshPopoverHidden();

		return this.getActionByPrefix(
			actions,
			'UUX.Client.Dashboard.ForceReload',
			async () => {
				await this.dataModelCacheService.resetCache();
				this.page.reloadPage();
			},
			isRefreshPopoverHidden ? undefined : this.i18n.getFormatted('shell.dashboards.cacheHint', this.configService.get('dataSourcesCacheLifetime')),
			isRefreshPopoverHidden ? undefined : () => this.dashboardService.setRefreshPopoverHidden()
		);
	}

	private async getDeleteAction(actions: MxAction[], dashboard: Dashboard) {
		const action = this.getOriginalAction(actions, 'UUX.Client.Dashboard.Delete');

		if (!action) {
			return null;
		}

		const user = await this.userProfileService.get();
		if (user.isDevMode || (dashboard.owner && (dashboard.isOwner || user.isAdmin)) || dashboard.isEditable) {
			action.customConfirmationTitle = this.i18n.getFormatted('solutionBuilderAgent.common.deleteTemplate', dashboard.title);
			const deleteAction = this.createAction({
				name: action.name,
				title: action.title,
				icon: action.icon,
				execute: async () => {
					const navItem = await this.navigationService.getNavigationItem(dashboard.navigationId);
					const visibleSiblings = navItem?.parent?.children?.filter((c) => !c.isHidden) ?? [];
					if (visibleSiblings.length <= 1) {
						await this.notificationService.alert(
							action.title,
							this.i18n.get('shell.dashboards.cannotDeleteLastVisibleDashboard') || 'Cannot delete the last visible dashboard. Unhide another one first.'
						);
						return;
					}

					const isConfirmed =
						!action.serviceOperation?.ConfirmationMessage ||
						(await this.showConfirmation(action.customConfirmationTitle ?? action.title, action.serviceOperation.ConfirmationMessage as string));
					if (!isConfirmed) return;

					const parentItem = navItem?.parent;
					await this.dashboardService.deleteDashboard(dashboard);
					const index = parentItem?.children?.findIndex((n) => n.id === dashboard.navigationId) ?? -1;
					await this.navigationService.setNavigationItemById(parentItem?.children?.[index - 1]?.id ?? parentItem?.id ?? this.navigationService.navItems[0].id);
					this.navigationService.removeItem(dashboard.navigationId, parentItem);
				},
			});
			return deleteAction;
		} else {
			return null;
		}
	}

	private getRemovePersonalizationAction(actions: MxAction[], dashboard: Dashboard) {
		return this.dashboardService.hasPersonalization(dashboard)
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.RemovePersonalization', async () => {
					await this.dashboardService.removePersonalization(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getRevertPublishAction(actions: MxAction[], dashboard: Dashboard) {
		return dashboard.isCustomized
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.RevertPublishedLayout', async () => {
					await this.dashboardService.removeCustomization(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getPublishAction(actions: MxAction[], dashboard: Dashboard) {
		return this.dashboardService.hasPersonalization(dashboard)
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.PublishLayout', async () => {
					await this.dashboardService.publishDashboard(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getSwitchBackAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.SwitchBack', () => this.dashboardService.dashboardLookOperation(dashboard.navigationId));
	}

	private getFullScreenAction(actions: MxAction[]) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.FullScreen', () => {
			this.page.setDisplayMode(DisplayMode.BigScreen, true);
			this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
				eventName: 'Dashboard fullscreen mode enabled',
			});
		});
	}

	private getEditAction(actions: MxAction[]) {
		const originalAction = this.getOriginalAction(actions, 'UUX.Client.Dashboard.Edit');

		if (!originalAction) return null;

		if (this.isDashboardLicensed) {
			return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Edit', async () => {
				this.page.enableConfigurationMode();
				this.dataModelCacheService.disable();
			});
		} else {
			return this.createAction({
				name: originalAction.name,
				title: originalAction.title,
				icon: originalAction.icon,
				execute: async () => {
					await this.dialogOpenerService.showLicenseUpgradeDialog();
				},
			});
		}
	}

	async getActions() {
		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(new ActionsContext([], []), [ActionContextType.None]));
		const dashboard = this.page.dashboard;

		return [
			this.getForceReloadAction(actions),
			this.getExportAction(actions, dashboard),
			this.getFullScreenAction(actions),
			this.getEditAction(actions),
			this.getSwitchBackAction(actions, dashboard),
			this.getPublishAction(actions, dashboard),
			this.getRevertPublishAction(actions, dashboard),
			this.getRemovePersonalizationAction(actions, dashboard),
			this.getShareAction(actions, dashboard),
			this.getSendAction(actions, dashboard),
			this.getPrintAction(actions, dashboard),
			this.getSubscribeAction(actions, dashboard),
			await this.getDeleteAction(actions, dashboard),
		].filter((x) => x !== null);
	}

	private async showWizard({
		navigationId,
		widgetId,
		widgetName,
		actionName,
		presetFields,
	}: {
		navigationId: string;
		widgetId: string;
		widgetName: string;
		actionName: string;
		presetFields?: AnyObject;
	}) {
		const objDetails = await this.entityService.getFragmentDescriptorsInBatch('PDRNavigationItemClassBase', navigationId);
		if (objDetails) {
			const navObjectId = objDetails.ObjectId;

			const wizardView = this.wizardService.getWizardView({
				widgetId: widgetId,
				wizardMode: WizardMode.Default,
				name: widgetName,
				displayName: widgetName,
				selectedObjects: [
					{
						_id: navObjectId,
						_type: 'PDRNavigationItemType',
						_displayName: this.page.dashboard.title,
					},
				],
				actionName: actionName,
				presetFields,
			});

			// @ts-expect-error no types
			wizardView.id = Date.now();
			this.sidePanelService.addView(wizardView);
		}
	}

	private createAction({
		name,
		title,
		icon,
		execute,
		popover,
		onPopoverClose,
	}: {
		name: string;
		title: string;
		icon: string;
		popover?: string;
		execute: () => Promise<void>;
		onPopoverClose?: () => void;
	}) {
		const action = new MxAction(name, title, icon);
		action.popover = popover;
		if (onPopoverClose) {
			action.onPopoverClose = onPopoverClose;
		}
		action.controller = function () {
			return {
				execute,
			};
		};
		return action;
	}

	async onAutoRefreshOptionChanged() {
		await this.page.setAutoRefresherInterval(this.page.dashboard.autoRefreshInterval);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Dashboard auto-refresh enabled',
			intervalHours: this.page.dashboard.autoRefreshInterval / 60,
		});
	}

	initSwitchingToNeighborDashboardsHandlers() {
		this.dashboardService.autoSwitchToNextDashboardInterval = this.dashboardService.getAutoSwitchToNextDashboardInterval();
		this.eventService.subscribeOnce(EventCodes.DashboardPageInitialized, async () => {
			this.featureTrackingService.track(FeatureTrackingType.viewReport, FeatureTrackingUseCases.reportingToolkit);

			const changingCurtainEl = $('.mx-dashboard-changing-curtain');
			if (changingCurtainEl.length) {
				this.page.setDisplayMode(DisplayMode.BigScreen);
				changingCurtainEl.animate({ opacity: 0 }, 1200, () => {
					changingCurtainEl.remove();
				});
				if (this.dashboardService.autoSwitchToNextDashboardInterval > 0) {
					this.runAutoSwitchingToNextDashboard();
				}
			} else {
				this.stopAutoSwitchingToNextDashboard();
			}

			const neighborDashboardsInfo = await this.dashboardService.getNeighborDashboardsInfo(this.page.dashboard.navigationId);
			this.isSwitchToNextDashboardAvailable = !!neighborDashboardsInfo;
			if (this.isSwitchToNextDashboardAvailable) {
				this.page.totalNeighborDashboards = neighborDashboardsInfo?.total;
				this.page.currentDashboardIndex = neighborDashboardsInfo?.currentIndex;
			}
		});

		this.eventService.subscribeTo(
			EventCodes.DisplayModeChanged,
			(e, displayMode, byHuman) => {
				if (byHuman) {
					if (displayMode === DisplayMode.BigScreen) {
						if (this.isSwitchToNextDashboardAvailable && this.dashboardService.autoSwitchToNextDashboardInterval > 0) {
							this.runAutoSwitchingToNextDashboard();
						}
					} else {
						this.stopAutoSwitchingToNextDashboard();
					}
				}
			},
			this.$scope
		);

		document.addEventListener('keydown', (event) => this.handleDashboardSwitchingKeydown(event));
	}

	destroySwitchingToNeighborDashboardsHandlers() {
		clearTimeout(this.autoSwitchingToNextDashboardTimeoutId);
		document.removeEventListener('keydown', (event) => this.handleDashboardSwitchingKeydown(event));
	}

	onAutoSwitchToNextDashboardOptionChanged() {
		this.dashboardService.setAutoSwitchToNextDashboardInterval(this.dashboardService.autoSwitchToNextDashboardInterval);
		if (this.dashboardService.autoSwitchToNextDashboardInterval > 0) {
			this.runAutoSwitchingToNextDashboard();
		} else {
			this.stopAutoSwitchingToNextDashboard();
		}
	}

	switchToNeighbourDashboard(isToNext: boolean /* true - next, false - prev */) {
		clearTimeout(this.autoSwitchingToNextDashboardTimeoutId);
		const changingCurtainEl = $('<div class="mx-dashboard-changing-curtain"></div>');
		$('body').append(changingCurtainEl);
		changingCurtainEl.css({ opacity: 0 }).animate({ opacity: 1 }, 600);
		this.autoSwitchingToNextDashboardTimeoutId = setTimeout(async () => {
			const neighborDashboardsInfo = await this.dashboardService.getNeighborDashboardsInfo(this.page.dashboard.navigationId);
			this.navigationService.setCurrentNavigationItem(neighborDashboardsInfo?.[isToNext ? 'nextNavItem' : 'prevNavItem']);
		}, 500);
	}

	switchToNextDashboard() {
		this.switchToNeighbourDashboard(true);
	}

	switchToPrevDashboard() {
		this.switchToNeighbourDashboard(false);
	}

	handleDashboardSwitchingKeydown(event: KeyboardEvent) {
		if (this.page.displayMode === DisplayMode.BigScreen && this.isSwitchToNextDashboardAvailable) {
			if (event.key === 'ArrowRight') {
				this.switchToNextDashboard();
				event.preventDefault();
			} else if (event.key === 'ArrowLeft') {
				this.switchToPrevDashboard();
				event.preventDefault();
			}
		}
	}

	runAutoSwitchingToNextDashboard() {
		clearTimeout(this.autoSwitchingToNextDashboardTimeoutId);
		this.autoSwitchingToNextDashboardTimeoutId = setTimeout(() => {
			this.switchToNeighbourDashboard(true);
		}, this.dashboardService.autoSwitchToNextDashboardInterval * 1000);
	}

	stopAutoSwitchingToNextDashboard() {
		clearTimeout(this.autoSwitchingToNextDashboardTimeoutId);
		$('.mx-dashboard-changing-curtain').remove();
	}
}

export const DashboardControlPanelComponent = {
	name: 'mxDashboardControlPanel',
	controller: DashboardControlPanelController,
	controllerAs: 'vm',
	template,
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
